import type { ReactNode } from 'react';
import type { WillBeneficiary } from '../../services/will.service';

const RELATIONSHIP_EN: Record<string, string> = {
  FATHER: 'Father',
  MOTHER: 'Mother',
  SIBLING: 'Sibling',
  SPOUSE: 'Spouse',
  CHILD: 'Child',
  FATHER_IN_LAW: 'Father-in-law',
  MOTHER_IN_LAW: 'Mother-in-law',
  GRANDPARENT_IN_LAW: 'Grandparent-in-law',
  AUNT_IN_LAW: 'Aunt-in-law',
  UNCLE_IN_LAW: 'Uncle-in-law',
  COUSIN_IN_LAW: 'Cousin-in-law',
};

const RELATIONSHIP_MY: Record<string, string> = {
  FATHER: 'Bapa',
  MOTHER: 'Ibu',
  SIBLING: 'Adik-beradik',
  SPOUSE: 'Pasangan',
  CHILD: 'Anak',
  FATHER_IN_LAW: 'Bapa mertua',
  MOTHER_IN_LAW: 'Ibu mertua',
  GRANDPARENT_IN_LAW: 'Datuk/ Nenek mertua',
  AUNT_IN_LAW: 'Mak cik mertua',
  UNCLE_IN_LAW: 'Pak cik mertua',
  COUSIN_IN_LAW: 'Sepupu mertua',
};

export function beneficiaryName(b: WillBeneficiary): string {
  return `${b.familyMember.firstName} ${b.familyMember.lastName ?? ''}`.trim();
}

export function relationshipLabel(b: WillBeneficiary, lang: 'en' | 'my'): string {
  const map = lang === 'en' ? RELATIONSHIP_EN : RELATIONSHIP_MY;
  return map[b.familyMember.relationship] ?? b.familyMember.relationship;
}

function formatLetterDate(lang: 'en' | 'my'): string {
  const locale = lang === 'en' ? 'en-GB' : 'ms-MY';
  return new Date().toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
}

const blank = '____________________';

interface HibahLetterProps {
  lang: 'en' | 'my';
  testator: {
    fullName: string;
    nricPassport: string | null;
    accountId: string | null;
  };
  beneficiaries: WillBeneficiary[];
  /** Paywall preview: render only the opening paragraphs. */
  preview?: boolean;
}

/**
 * Printable Letter of Conditional Hibah / Wasiat, reproduced from
 * docs/draft_will-en.rtf / draft_will-my.rtf. The RM figure is deliberately
 * dynamic — the letter states each beneficiary's percentage of the funds
 * standing in the account at execution time (product decision, 2026-09).
 */
export default function HibahLetter({ lang, testator, beneficiaries, preview }: HibahLetterProps) {
  const my = lang === 'my';
  const nric = testator.nricPassport || blank;

  const P = ({ children }: { children: ReactNode }) => (
    <p className="mb-3 text-justify">{children}</p>
  );

  return (
    <div className="print-letter mx-auto w-full max-w-[210mm] bg-white p-10 font-serif text-sm leading-relaxed text-neutral-900">
      <h1 className="mb-6 text-center text-base font-bold tracking-wide">
        {my ? 'SURAT HIBAH BERSYARAT / WASIAT' : 'LETTER OF CONDITIONAL HIBAH / WASIAT'}
      </h1>

      <p className="mb-1">{my ? 'Daripada (Pemberi Hibah / Pewasiat):' : 'From (Hibah Provider / Testator):'}</p>
      <p className="mb-1">{my ? 'Nama Penuh:' : 'Full Name:'} <span className="font-semibold">{testator.fullName}</span></p>
      <p className="mb-1">{my ? 'No. Kad Pengenalan / Pasport:' : 'NRIC / Passport No.: '} <span className="font-semibold">{nric}</span></p>
      <p className="mb-4">{my ? 'ID Pengguna / Akaun Akhirat.co:' : 'Akhirat.co User / Account ID:'} <span className="font-semibold">{testator.accountId || blank}</span></p>

      <p className="mb-2">{my ? 'Kepada (Penerima / Benefisiari):' : 'To (Beneficiary / Recipient):'}</p>
      <ol className="mb-4 list-decimal pl-6">
        {beneficiaries.map((b, i) => (
          <li key={b.id} className="mb-2">
            <p>{my ? 'Nama Penuh:' : 'Full Name:'} <span className="font-semibold">{beneficiaryName(b)}</span></p>
            <p className="pl-4">{my ? 'No. Kad Pengenalan / Pasport:' : 'NRIC / Passport No.: '} {b.familyMember.passportNumber || blank}</p>
            <p className="pl-4">{my ? 'ID Benefisiari:' : 'Beneficiary ID:'} <span className="font-semibold">{b.familyMember.memberId}</span></p>
            <p className="pl-4">{my ? 'Hubungan:' : 'Relationship:'} {relationshipLabel(b, lang)}</p>
            {i === beneficiaries.length - 1 ? null : <span />}
          </li>
        ))}
      </ol>

      <p className="mb-6">{my ? 'Tarikh:' : 'Date:'} {formatLetterDate(lang)}</p>

      <h2 className="mb-3 text-sm font-bold">
        {my ? 'PENGISYTIHARAN HIBAH BERSYARAT / WASIAT' : 'DECLARATION OF CONDITIONAL HIBAH / WASIAT'}
      </h2>
      <P>
        {my
          ? `Saya, ${testator.fullName}, No. Kad Pengenalan/Pasport ${nric}, dengan ini mengisytiharkan bahawa saya membuat pengisytiharan ini secara sukarela, dalam keadaan sedar dan tanpa sebarang paksaan atau pengaruh tidak wajar daripada mana-mana pihak.`
          : `I, ${testator.fullName}, NRIC/Passport No. ${nric}, hereby declare that I am making this declaration voluntarily, consciously and without coercion or undue influence.`}
      </P>
      <P>
        {my
          ? 'Tertakluk kepada terma dan syarat yang dinyatakan di dalam dokumen ini, saya berhasrat untuk memberikan dan/atau mewasiatkan kepada Benefisiari yang dinamakan di atas jumlah wang yang dinyatakan di bawah daripada dana yang berada dalam akaun Akhirat.co saya, tertakluk kepada terma akaun berkenaan dan undang-undang Malaysia yang terpakai.'
          : 'Subject to the terms and conditions set out herein, I intend to give and/or bequeath to the above-named Beneficiary(ies) the amount specified below from the funds standing to my credit in my Akhirat.co account, subject to the applicable terms of the account and applicable laws of Malaysia.'}
      </P>

      {preview && (
        <p className="mt-6 text-center text-xs uppercase tracking-[0.2em] text-neutral-400">
          {my ? '— Selanjutnya dalam versi penuh —' : '— Continued in the full document —'}
        </p>
      )}
      {preview ? null : (
        <>
      <h3 className="mb-2 text-sm font-bold">{my ? 'Jumlah Yang Ditetapkan' : 'Designated Amount'}</h3>
      {beneficiaries.map((b) => (
        <div key={`amt-${b.id}`} className="mb-3 pl-4">
          <p className="mb-0.5">{my ? 'Benefisiari:' : 'Beneficiary:'} <span className="font-semibold">{beneficiaryName(b)}</span></p>
          <p className="mb-0.5">{my ? 'Peratusan:' : 'Percentage:'} <span className="font-semibold">{Number(b.percentage)}%</span></p>
          <p>
            {my ? 'Jumlah Ditetapkan:' : 'Designated Amount:'}{' '}
            {my
              ? `RM [•] — iaitu ${Number(b.percentage)}% daripada dana yang berada dalam akaun Akhirat.co saya pada tarikh pelaksanaan wasiat ini`
              : `RM [•] — being ${Number(b.percentage)}% of the funds standing to my credit in my Akhirat.co account at the time of execution`}
          </p>
        </div>
      ))}

      <P>
        {my
          ? 'Jumlah tersebut adalah dimaksudkan untuk diagihkan kepada Benefisiari yang dinamakan apabila berlakunya peristiwa atau keadaan yang dinyatakan dalam pengisytiharan ini serta tertakluk kepada kesahan dan kebolehkuatkuasaan dokumen ini di bawah undang-undang Malaysia yang terpakai.'
          : 'The above amount is intended to be distributed to the named Beneficiary upon the occurrence of the event or circumstances specified under this declaration and subject to the validity and enforceability of this document under applicable Malaysian law.'}
      </P>
      <P>
        {my
          ? "Sekiranya terdapat lebih daripada seorang Benefisiari, hak atau bahagian setiap Benefisiari hendaklah sebagaimana yang dinyatakan secara jelas di dalam dokumen ini atau jadual yang dilampirkan."
          : "If there is more than one Beneficiary, each Beneficiary’s entitlement shall be as expressly stated above or in the attached schedule."}
      </P>
      <P>
        {my
          ? 'Pengisytiharan ini dibuat bagi merekodkan hasrat saya yang sebenar dan secara sukarela berhubung pengagihan dana yang ditetapkan. Tiada apa-apa dalam dokumen ini boleh ditafsirkan sebagai mengatasi mana-mana kehendak mandatori undang-undang Malaysia, prinsip Syariah yang terpakai, perintah mahkamah atau terma sah yang mengawal akaun atau aset berkenaan.'
          : 'This declaration is intended to reflect my genuine and voluntary intention regarding the distribution of the designated funds. Nothing in this document shall be interpreted as overriding any mandatory requirement of Malaysian law, applicable Shariah principles, court order, or the lawful terms governing the relevant account or asset.'}
      </P>

      <h2 className="mb-3 text-sm font-bold">{my ? 'PENERIMAAN OLEH BENEFISIARI' : 'ACCEPTANCE BY BENEFICIARY'}</h2>
      {beneficiaries.map((b) => {
        const bName = beneficiaryName(b);
        const bNric = b.familyMember.passportNumber || blank;
        return (
          <div key={`accept-${b.id}`} className="mb-6">
            <P>
              {my
                ? `Saya, ${bName}, No. Kad Pengenalan/Pasport ${bNric}, dengan ini mengakui bahawa saya telah membaca dan memahami pengisytiharan ini dan bersetuju untuk menerima Hibah/Wasiat tersebut setakat mana ia sah dan boleh dikuatkuasakan di sisi undang-undang.`
                : `I, ${bName}, NRIC/Passport No. ${bNric}, hereby acknowledge that I have read and understood this declaration and agree to accept the above Hibah/Wasiat to the extent that it is legally valid and enforceable.`}
            </P>
            <P>
              {my
                ? 'Saya mengesahkan bahawa saya tidak mempunyai sebarang tuntutan atau permintaan tambahan berhubung jumlah yang ditetapkan tersebut selain daripada hak atau bahagian yang dinyatakan secara jelas dalam pengisytiharan ini, tertakluk kepada undang-undang yang terpakai.'
                : 'I confirm that I have no further claim or request in respect of the designated amount beyond the entitlement expressly stated in this declaration, subject always to applicable law.'}
            </P>
            <div>
              <p className="mb-1">{my ? 'Tandatangan Benefisiari:' : "Beneficiary’s Signature:"} {blank}</p>
              <p className="mb-1">{my ? 'Nama:' : 'Name:'} <span className="font-semibold">{bName}</span></p>
              <p>{my ? 'Tarikh:' : 'Date:'} {formatLetterDate(lang)}</p>
            </div>
          </div>
        );
      })}

      <h2 className="mb-3 text-sm font-bold">{my ? 'TANDATANGAN PEMBERI HIBAH / PEWASIAT' : "DECLARANT’S SIGNATURE"}</h2>
      <P>
        {my
          ? 'Saya mengesahkan bahawa saya telah membaca dan memahami dokumen ini dan bahawa saya membuat pengisytiharan ini secara sukarela serta dengan pengetahuan penuh mengenai kandungannya.'
          : 'I confirm that I have read and understood this document and that I make this declaration voluntarily and with full knowledge of its contents.'}
      </P>
      <div className="mb-6">
        <p className="mb-1">{my ? 'Tandatangan:' : 'Signature of Declarant:'} {blank}</p>
        <p className="mb-1">{my ? 'Nama:' : 'Name:'} <span className="font-semibold">{testator.fullName}</span></p>
        <p>{my ? 'Tarikh:' : 'Date:'} {blank}</p>
      </div>

      <h2 className="mb-3 text-sm font-bold">{my ? 'SAKSI' : 'WITNESSES'}</h2>
      {[1, 2].map((n) => (
        <div key={n} className="mb-4">
          <p className="mb-1 font-semibold">{my ? `Saksi ${n}` : `Witness ${n}`}</p>
          <p className="mb-1">{my ? 'Nama:' : 'Name:'} {blank}</p>
          <p className="mb-1">{my ? 'No. Kad Pengenalan / Pasport:' : 'NRIC / Passport No.: '} {blank}</p>
          <p className="mb-1">{my ? 'Tandatangan:' : 'Signature:'} {blank}</p>
          <p>{my ? 'Tarikh:' : 'Date:'} {blank}</p>
        </div>
      ))}
        </>
      )}
    </div>
  );
}
